/* eslint-disable no-alert */
import React, { useContext, useState } from "react";
import { PropTypes } from 'prop-types';
import { Button, message } from "antd";
import axios from "axios";

import { SubsContext } from "../../components/Billsby/SubscriptionInfo";
import { isFeatureEnabled } from "../../config/featureFlags";
import { apiUrl } from "../../config/properties";
import { makeGraphQLRequest } from "../../utils/requestUtils";

// eslint-disable-next-line react/prop-types
const AceButton = ({ items, auth }) => {
  const {
    subscriptionLevel,
  } = useContext(SubsContext);
  const [loading, setLoading] = useState(false);

  const addToAceFolder = async (solicitationId, userId) => {
    try {
      // First, get current folders
      const getFoldersQuery = `query user($id: ID) {
        user(id: $id) {
          folders {
            flagged
            tbq
            inprocess
            archived
            ace
          }
        }
      }`;

      const getFoldersResponse = await makeGraphQLRequest({
        query: getFoldersQuery,
        variables: { id: userId },
      });

      const currentFolders = getFoldersResponse.user.folders || {
        flagged: [],
        tbq: [],
        inprocess: [],
        archived: [],
        ace: [],
      };

      // Add solicitation to ACE folder if not already there
      if (!currentFolders.ace) {
        currentFolders.ace = [];
      }
      if (currentFolders.ace.indexOf(solicitationId) === -1) {
        currentFolders.ace.push(solicitationId);
      }

      // Update folders
      const updateFoldersMutation = `mutation updateFolders($criteria: FoldersInput, $userId: ID) {
        updateFolders(folders: $criteria, userId: $userId) {
          folders {
            ace
          }
        }
      }`;

      await makeGraphQLRequest({
        query: updateFoldersMutation,
        variables: { criteria: currentFolders, userId },
      });

      console.log('Solicitation added to ACE folder');
      console.log('ACE folder contents:', currentFolders.ace);
      console.log('Solicitation ID stored:', solicitationId);
    } catch (error) {
      console.error('Error adding solicitation to ACE folder:', error);
      // Don't show error to user as the main operation (sending to ACE) succeeded
    }
  };

  const sendToAce = async () => {
    if (!isFeatureEnabled('ENABLE_BILLSBY') || subscriptionLevel !== 'Full') {
      message.error("You must have a Subscription Level of 'Full' to access this feature.");
      return;
    }

    setLoading(true);

    try {
      // Extract unitCost from procurement history (most recent entry)
      let unitCost = null;
      if (items.procurementHistory && items.procurementHistory.length > 0) {
        // Sort by awardedDate descending to get most recent
        const sortedHistory = [...items.procurementHistory].sort((a, b) => {
          const dateA = new Date(a.awardedDate || 0);
          const dateB = new Date(b.awardedDate || 0);
          return dateB - dateA;
        });
        unitCost = sortedHistory[0].unitCost;
      }

      // Extract standardCost from solicitationTab
      let standardCost = null;
      if (items.solicitationTab && items.solicitationTab.length > 0) {
        // Get the first solicitationTab entry's standardCost
        standardCost = items.solicitationTab[0].standardCost;
      }

      // Extract solicitation data from items
      const solicitationData = {
        solicitationNum: items.solicitationNum || items.id,
        nomenclature: items.nomenclature || (items.purchaseRequest && items.purchaseRequest.nomenclature ? items.purchaseRequest.nomenclature : ''),
        nsn: items.nsn || (items.purchaseRequest && items.purchaseRequest.nsnPart ? items.purchaseRequest.nsnPart : ''),
        partNumber: items.partNumber || (items.purchaseRequest && items.purchaseRequest.number ? items.purchaseRequest.number : ''),
        quantity: items.quantity || (items.purchaseRequest && items.purchaseRequest.quantity ? items.purchaseRequest.quantity : 0),
        delivery: items.delivery || items.issueDate || '',
        um: items.um || '', // Unit of measure for ACE mapping
        estValue: items.estValue || '', // Estimated value for ACE mapping (fallback)
        unitCost, // Unit cost from most recent procurement history
        standardCost, // Standard cost from solicitationTab
        prgid: items.prgid || '', // Program ID if available
        setAside: items.setAside || (items.purchaseRequest && items.purchaseRequest.setAside ? items.purchaseRequest.setAside : ''),
        packagingInfo: items.packagingInfo || '', // Packaging information
        amc: items.amc || '', // AMC information
        procurementHistory: items.procurementHistory || [], // Procurement history
        techInfo: items.techInfo || '', // Technical information
      };

      // Debug logging for data extraction
      console.log('=== ACE DATA EXTRACTION DEBUG ===');
      console.log('Full items object:', items);
      console.log('um from items:', items.um);
      console.log('um type:', typeof items.um);
      console.log('estValue from items:', items.estValue);
      console.log('estValue type:', typeof items.estValue);
      console.log('estValue length:', items.estValue ? items.estValue.length : 'undefined');
      console.log('unitCost extracted:', unitCost);
      console.log('unitCost type:', typeof unitCost);
      console.log('standardCost extracted:', standardCost);
      console.log('standardCost type:', typeof standardCost);
      console.log('procurementHistory length:', items.procurementHistory ? items.procurementHistory.length : 'undefined');
      console.log('solicitationTab length:', items.solicitationTab ? items.solicitationTab.length : 'undefined');
      console.log('All items keys:', Object.keys(items));
      console.log('Extracted solicitation data:', solicitationData);
      console.log('=== END ACE DEBUG ===');

      // Validate and prepare user/org IDs
      const userId = auth.id;
      const organizationId = auth.organizationId;

      // Check if we have valid user data
      if (!userId) {
        message.error('User ID is required to send solicitation to ACE.');
        return;
      }

      if (!organizationId) {
        message.error('Organization ID is required to send solicitation to ACE.');
        return;
      }

      // Prepare the request payload
      const payload = {
        solicitation: solicitationData,
        prgid: items.prgid || 'DEFAULT-PRGID', // Provide default if missing
        userId,
        organizationId,
      };

      // Debug logging for payload
      console.log('ACE Payload Debug:', {
        payload,
        hasSolicitation: !!payload.solicitation,
        hasPrgid: !!payload.prgid,
        hasUserId: !!payload.userId,
        hasOrganizationId: !!payload.organizationId,
        actualUserId: userId,
        actualOrganizationId: organizationId
      });

      // Get the API endpoint - use the serverless API, not tomcat host
      const baseApiUrl = apiUrl.replace('/api/', ''); // Remove /api/ suffix to get base URL
      const endpoint = `${baseApiUrl}/ace/send-solicitation`;

      console.log('ACE API Endpoint:', endpoint);

      // Make the API call
      const response = await axios.post(endpoint, payload, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${auth.jwtToken || localStorage.getItem('token')}`,
        },
      });

      console.log('ACE Response:', response.data);

      if (response.data.success) {
        message.success('Solicitation sent to ACE successfully!');
        // Add solicitation to ACE folder using solicitationNum first, then ID fallback
        await addToAceFolder(items.solicitationNum || items.id, auth.id);

        // Hint other tabs/pages to refresh counts (listen for this if desired)
        try {
          window.localStorage.setItem('aceFolderUpdatedAt', String(Date.now()));
          window.dispatchEvent(new Event('storage'));
        } catch (e) {
          // no-op
        }
      } else {
        console.error('ACE API Error:', response.data);
        message.error(`Failed to send solicitation to ACE: ${response.data.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error sending to ACE:', error);

      if (error.response) {
        console.error('Error Response:', {
          status: error.response.status,
          data: error.response.data,
          headers: error.response.headers
        });
        message.error(`Error sending solicitation to ACE: ${error.response.data && error.response.data.error || error.response.statusText || 'Unknown error'}`);
      } else {
        message.error('Error sending solicitation to ACE. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleOnClick = () => {
    sendToAce();
  }

  // Debug logging
  console.log('ACE Button Debug:', {
    isFeatureEnabled: isFeatureEnabled('ENABLE_ACE'),
    subscriptionLevel,
    items: items ? 'present' : 'missing',
    auth: auth ? 'present' : 'missing',
    jwtToken: auth && auth.jwtToken ? 'present' : 'missing',
    authKeys: auth ? Object.keys(auth) : 'no auth object'
  });

  if (isFeatureEnabled('ENABLE_ACE')) {
    return (
      <Button
        id="sndAce"
        onClick={handleOnClick}
        loading={loading}
        type="primary"
        style={{
          background: '#ff7c52',
          border: '#ff7c52',
          borderRadius: '6px',
        }}
      >
        Send to A.C.E
      </Button>
    )
  }
  return (<span />);
}

AceButton.propType = {
  auth: PropTypes.object.isRequired,
  items: PropTypes.object.isRequired,
}

export default AceButton;


// WEBPACK FOOTER //
// ./src/pages/SolicitationDetail/AceButton.jsx